import { Schema, schema, normalize, NormalizedSchema } from 'normalizr';
import kebabCase from 'lodash/kebabCase';
import { API_URL } from '@app/api';
import {
  createSuccesAction,
  getOneSuccessAction,
  getListSuccessAction,
  updateSuccessAction,
  replaceSuccessAction,
  deleteOneSuccessAction,
  optimisticUpdate,
  optimisticDiscard,
} from './actions';
import { createApiException } from './exceptions';
import { fetchWithAuth } from './Auth';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { createDenormalizeSelector, plainEntityByIdSelector } from './entities';
import { Logger } from '../../lib/logger';
import { formatIdForDeserialization } from './formatIdForDeserialization';

interface UrlOptions {
  entity?: string;
  path?: string;
  id?: string;
}

export const generateUrl = (options: UrlOptions): string => {
  let path = options.path;

  if (!path && options.entity) {
    path = `/${kebabCase(options.entity)}`;
    if (options.id) {
      path += `/${options.id}`;
    }
  } else if (!path) {
    path = '';
  }

  return API_URL + path;
};

interface GenericOptions {
  entity: string;
  params?: Object;
  path?: string;
  schema?: Schema<any>;
  headers?: { [name: string]: string };
  ignoreDbUpdate?: boolean;
  noLogout?: boolean;
}

interface FetchThunkOptions extends GenericOptions {
  id?: string;
  list?: boolean;
  fetchOptions: AxiosRequestConfig;
  successAction: (
    normalizedResults: NormalizedSchema<any, any>,
    options: FetchThunkOptions,
  ) => any;
}

const createFetchThunk = <T>(options: FetchThunkOptions) => {
  const { entity, path, id } = options;
  return async (dispatch: Function, getState: () => any): Promise<T> => {
    const url = generateUrl({ entity, path, id });

    try {
      const results: AxiosResponse<T> = await dispatch(
        fetchWithAuth(
          {
            url,
            params: options.params,
            headers: options.headers,
            ...options.fetchOptions,
          },
          options.noLogout,
        ),
      );

      if (!results.data || options.ignoreDbUpdate) {
        return results.data;
      }

      const normalizationSchema = !options.schema
        ? options.list
          ? new schema.Array(new schema.Entity(entity))
          : new schema.Entity(entity)
        : options.schema;

      const normalizedResults = normalize(
        formatIdForDeserialization(results.data || {}),
        normalizationSchema,
      );
      dispatch(options.successAction(normalizedResults, options));

      if (results.status === 204) {
        return results.data;
      }

      const denormalizeSelector =
        createDenormalizeSelector<T>(normalizationSchema);
      const selected = denormalizeSelector(
        getState(),
        normalizedResults.result,
      );
      if (!selected) {
        Logger.assertionFailed("Didn't find selected entity is the state", {
          results: normalizedResults.result,
          state: getState(),
        });
        return results.data;
      }
      return selected;
    } catch (e) {
      throw createApiException(e as any);
    }
  };
};

interface GetListOptions extends GenericOptions {}
export function getList<T>(options: GetListOptions) {
  return createFetchThunk<T[]>({
    ...options,
    list: true,
    successAction: getListSuccessAction,
    fetchOptions: {
      method: 'GET',
    },
  });
}

export interface GetOneOptions extends GenericOptions {
  id?: string;
}
export function getOne<T>(options: GetOneOptions) {
  return createFetchThunk<T>({
    ...options,
    successAction: getOneSuccessAction,
    fetchOptions: {
      method: 'GET',
    },
  });
}

interface CreateOptions extends GenericOptions {
  payload?: Object;
  fetchOptions?: Partial<AxiosRequestConfig>;
}
export function create<T>(options: CreateOptions) {
  return createFetchThunk<T>({
    ...options,
    successAction: createSuccesAction,
    fetchOptions: {
      method: 'POST',
      data: options.payload,
      ...options.fetchOptions,
    },
  });
}

interface ReplaceOptions extends GenericOptions {
  payload?: Object;
  id?: string;
}
export function replace<T>(options: ReplaceOptions) {
  return createFetchThunk<T>({
    ...options,
    successAction: replaceSuccessAction,
    fetchOptions: {
      method: 'PUT',
      data: options.payload,
    },
  });
}

interface UpdateOptions<T> extends GenericOptions {
  payload?: Object;
  id?: string;
  optimistic?: Partial<T>;
  fetchOptions?: AxiosRequestConfig;
}
export function update<T>(options: UpdateOptions<T>) {
  return async (dispatch: Function, getState: () => any) => {
    const fetchThunk = createFetchThunk<T>({
      ...options,
      successAction: updateSuccessAction,
      fetchOptions: {
        method: 'PATCH',
        data: options.payload,
        ...options.fetchOptions,
      },
    });

    if (options.optimistic && options.id) {
      const original = plainEntityByIdSelector(
        getState(),
        options.entity,
        options.id,
      );
      dispatch(
        optimisticUpdate(options.entity, options.id, options.optimistic),
      );
      try {
        const res = await dispatch(fetchThunk);
        return res;
      } catch (e) {
        dispatch(optimisticDiscard(options.entity, options.id, original));
        throw e;
      }
    }
    return dispatch(fetchThunk);
  };
}

interface DeleteOptions extends GenericOptions {
  id?: string;
}
export function deleteOne<T>(options: DeleteOptions) {
  return createFetchThunk<T>({
    ...options,
    successAction: deleteOneSuccessAction,
    fetchOptions: {
      method: 'DELETE',
    },
  });
}
